import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import {
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Tooltip,
  Typography,
} from "@material-ui/core";

import {
  Search,
  AddCommentOutlined,
  MoreVert,
  Clear as ClearIcon,
  Event as EventIcon,
} from "@material-ui/icons";

import toastError from "../../errors/toastError";
import api from "../../services/api";
import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsListCustom";
import TicketsListGroup from "../TicketsListGroup";
import TabPanel from "../TabPanel";
import { TagsFilter } from "../TagsFilter";
import { UsersFilter } from "../UsersFilter";
import TicketsQueueSelect from "../TicketsQueueSelect";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import usePlans from "../../hooks/usePlans";

const useStyles = makeStyles((theme) => ({
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },

  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px 8px",
    backgroundColor: theme.palette.background.paper,
    flexWrap: "wrap",
    gap: 4,
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px 6px",
    },
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: theme.palette.text.primary,
    letterSpacing: "-0.3px",
    [theme.breakpoints.down("sm")]: {
      fontSize: 18,
    },
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  headerIcon: {
    color: theme.palette.sidebarIcon,
    padding: 8,
  },

  searchContainer: {
    display: "flex",
    padding: "4px 12px 8px",
    backgroundColor: theme.palette.background.paper,
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      padding: "4px 8px 6px",
    },
  },
  searchInputWrapper: {
    flex: 1,
    backgroundColor: theme.palette.background.default,
    display: "flex",
    borderRadius: 8,
    alignItems: "center",
    padding: "0 8px",
  },
  searchIcon: {
    color: theme.palette.text.secondary,
    fontSize: 20,
    marginRight: 6,
  },
  searchInput: {
    flex: 1,
    border: "none",
    padding: "8px 4px",
    outline: "none",
    backgroundColor: "transparent",
    color: theme.palette.text.primary,
    fontSize: 14,
    fontFamily: "inherit",
    "&::placeholder": {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },

  chipsContainer: {
    display: "flex",
    gap: 6,
    padding: "4px 12px 10px",
    overflowX: "auto",
    flexWrap: "wrap",
    backgroundColor: theme.palette.background.paper,
    "&::-webkit-scrollbar": { display: "none" },
    [theme.breakpoints.down("sm")]: {
      padding: "4px 8px 8px",
      gap: 4,
      flexWrap: "nowrap",
    },
  },
  chip: {
    borderRadius: 20,
    fontWeight: 500,
    fontSize: 13,
    height: 32,
    flexShrink: 0,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.secondary,
    border: "none",
    "&:hover": {
      backgroundColor: theme.palette.sidebarHover,
    },
    "&:focus": {
      backgroundColor: theme.palette.background.default,
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
      height: 28,
    },
  },
  chipActive: {
    backgroundColor: "rgba(255,53,0,0.8)",
    color: "#fff",
    "&:hover": {
      backgroundColor: "rgba(255,53,0,0.9)",
    },
    "&:focus": {
      backgroundColor: "rgba(255,53,0,0.8)",
    },
  },
  chipAI: {
    borderRadius: 20,
    fontWeight: 500,
    fontSize: 13,
    height: 32,
    flexShrink: 0,
    backgroundColor: "rgba(156, 39, 176, 0.12)",
    color: "#9c27b0",
    border: "1px solid rgba(156, 39, 176, 0.3)",
    "&:hover": {
      backgroundColor: "rgba(156, 39, 176, 0.2)",
    },
    "&:focus": {
      backgroundColor: "rgba(156, 39, 176, 0.12)",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
      height: 28,
    },
  },
  chipAIActive: {
    backgroundColor: "rgba(156, 39, 176, 0.85)",
    color: "#fff",
    border: "1px solid #9c27b0",
    "&:hover": {
      backgroundColor: "rgba(156, 39, 176, 0.95)",
    },
    "&:focus": {
      backgroundColor: "rgba(156, 39, 176, 0.85)",
    },
  },

  snackbar: {
    display: "flex",
    justifyContent: "space-between",
    backgroundColor: theme.palette.primary.main,
    color: "white",
    borderRadius: 30,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8em",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "1em",
    },
  },
  yesButton: {
    backgroundColor: "#FFF",
    color: "rgba(0, 100, 0, 1)",
    padding: "4px 4px",
    fontSize: "1em",
    fontWeight: "bold",
    textTransform: "uppercase",
    marginRight: theme.spacing(1),
    "&:hover": {
      backgroundColor: "darkGreen",
      color: "#FFF",
    },
    borderRadius: 30,
  },
  noButton: {
    backgroundColor: "#FFF",
    color: "rgba(139, 0, 0, 1)",
    padding: "4px 4px",
    fontSize: "1em",
    fontWeight: "bold",
    textTransform: "uppercase",
    "&:hover": {
      backgroundColor: "darkRed",
      color: "#FFF",
    },
    borderRadius: 30,
  },

  badge: {
    right: 0,
  },
  show: {
    display: "block",
  },
  hide: {
    display: "none !important",
  },
  unifiedScroll: {
    flex: 1,
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const TicketsManagerTabs = () => {
  const classes = useStyles();
  const history = useHistory();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [tab, setTab] = useState("open");
  const [tabOpen, setTabOpen] = useState("all");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const searchInputRef = useRef();
  const dateInputRef = useRef();
  const { user } = useContext(AuthContext);
  const { profile } = user;

  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const userQueueIds = user.queues.map((q) => q.id);
  const userQueueOptionIds = user.queueOptions?.map((q) => q.id) || [];
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
  const [selectedQueueOptionIds, setSelectedQueueOptionIds] = useState(userQueueOptionIds || []);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const [setClosedBox, setClosed] = useState(false);
  const [setGroupBox, setGroup] = useState(false);
  const [setNewsletterBox, setNewsletter] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [hasAIPlan, setHasAIPlan] = useState(false);
  const [aiCount, setAiCount] = useState(0);
  const { getPlanCompany } = usePlans();
  const companyId = user.companyId;

  useEffect(() => {
    async function fetchData() {
      let settingIndex;
      try {
        const { data } = await api.get("/settings/");
        settingIndex = data.filter((s) => s.key === "viewclosed");
      } catch (err) {
        toastError(err);
      }
      if (settingIndex[0]?.value === "enabled") {
        setClosed(true);
      } else {
        if (user.profile === "admin") {
          setClosed(true);
        } else {
          setClosed(false);
        }
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    async function fetchData() {
      let settingIndex;
      try {
        const { data } = await api.get("/settings/");
        settingIndex = data.filter((s) => s.key === "viewgroups");
      } catch (err) {
        toastError(err);
      }
      if (settingIndex[0]?.value === "enabled") {
        setGroup(true);
      } else {
        if (user.profile === "admin") {
          setGroup(true);
        } else {
          setGroup(false);
        }
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    async function fetchData() {
      let settingIndex;
      try {
        const { data } = await api.get("/settings/");
        settingIndex = data.filter((s) => s.key === "viewnewsletter");
      } catch (err) {
        toastError(err);
      }
      if (settingIndex[0]?.value === "enabled") {
        setNewsletter(true);
      } else {
        if (user.profile === "admin") {
          setNewsletter(true);
        } else {
          setNewsletter(false);
        }
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
      if (user.profile.toUpperCase() === "ADMIN" || user.allTicket === "enabled") {
        setShowAllTickets(true);
      }
    }, [user.profile, user.allTicket]);

  useEffect(() => {
    async function checkAIPlan() {
      try {
        const planConfigs = await getPlanCompany(undefined, companyId);
        setHasAIPlan(Boolean(planConfigs?.plan?.useOpenAi));
      } catch (e) {
        setHasAIPlan(false);
      }
    }
    checkAIPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();
    setSearchParam(searchedTerm);
    if (searchedTerm === "" && !selectedDate) {
      setTab("open");
    } else if (tab !== "search") {
      setTab("search");
    }
  };

  const handleDateChange = (e) => {
    const val = e.target.value;
    setSelectedDate(val);
    if (val) {
      if (tab !== "search") setTab("search");
    } else if (searchParam === "") {
      setTab("open");
    }
  };

  const handleSnackbarOpen = () => {
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const CloseAllTicket = async () => {
    try {
      await api.post("/tickets/closeAll", {
        status: tabOpen === "all" ? "open" : tabOpen,
        selectedQueueIds,
        selectedQueueOptionIds,
      });
      handleSnackbarClose();
    } catch (err) {
      console.log("Error: ", err);
    }
  };

  const applyPanelStyle = (status) => {
    if (tabOpen === "all") {
      // In 'all' mode, the lists are inline inside a shared unifiedScroll container,
      // so we don't apply rigid flex/height constraints here anymore.
      return {};
    }
    if (tabOpen !== status) {
      return { width: 0, height: 0, display: "none" };
    }
    return { flex: 1, height: "100%" };
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleSelectedTags = (selecteds) => {
    const tags = selecteds.map((t) => t.id);
    setSelectedTags(tags);
  };

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);
    setSelectedUsers(users);
  };

  const handleChipClick = (newTab, newTabOpen) => {
    setSearchParam("");
    setTab(newTab);
    if (newTabOpen) setTabOpen(newTabOpen);
  };

  const isChipActive = (targetTab, targetTabOpen) => {
    if (searchParam) return false;
    if (targetTabOpen) return tab === targetTab && tabOpen === targetTabOpen;
    return tab === targetTab;
  };

  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />

      <Snackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={i18n.t("tickets.inbox.closedAllTickets")}
        ContentProps={{ className: classes.snackbar }}
        action={
          <>
            <Button className={classes.yesButton} size="small" onClick={CloseAllTicket}>
              {i18n.t("tickets.inbox.yes")}
            </Button>
            <Button className={classes.noButton} size="small" onClick={handleSnackbarClose}>
              {i18n.t("tickets.inbox.no")}
            </Button>
          </>
        }
      />

      {/* Header */}
      <div className={classes.header}>
        <Typography className={classes.headerTitle}>Conversas</Typography>
        <div className={classes.headerActions}>
          <Tooltip title="Nova conversa">
            <IconButton
              className={classes.headerIcon}
              onClick={() => setNewTicketModalOpen(true)}
              size="small"
            >
              <AddCommentOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Mais opções">
            <IconButton
              className={classes.headerIcon}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              size="small"
            >
              <MoreVert />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Can
              role={user.profile}
              perform="tickets-manager:showall"
              yes={() => (
                <MenuItem>
                  <FormControlLabel
                    label={i18n.t("tickets.buttons.showAll")}
                    labelPlacement="start"
                    style={{ marginLeft: 0, marginRight: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={showAllTickets}
                        onChange={() => setShowAllTickets((prev) => !prev)}
                        name="showAllTickets"
                        color="primary"
                      />
                    }
                  />
                </MenuItem>
              )}
            />
            {user.profile === "admin" && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  handleSnackbarOpen();
                }}
              >
                {i18n.t("tickets.inbox.closedAll")}
              </MenuItem>
            )}
            <MenuItem onClick={() => setMenuAnchor(null)}>
              <TicketsQueueSelect
                selectedQueueIds={selectedQueueIds}
                selectedQueueOptionIds={selectedQueueOptionIds}
                userQueues={user?.queues}
                userQueueOptions={user?.queueOptions}
                onChange={(qIds, qoIds) => {
                  setSelectedQueueIds(qIds);
                  setSelectedQueueOptionIds(qoIds);
                }}
              />
            </MenuItem>
          </Menu>
        </div>
      </div>

      {/* Search */}
      <div className={classes.searchContainer}>
        <div className={classes.searchInputWrapper}>
          <Search className={classes.searchIcon} />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={i18n.t("tickets.search.placeholder")}
            className={classes.searchInput}
            value={searchParam}
            onChange={handleSearch}
          />
          {searchParam && (
            <IconButton
              size="small"
              onClick={() => {
                setSearchParam("");
                if (!selectedDate) setTab("open");
              }}
              style={{ padding: 4 }}
            >
              <ClearIcon style={{ fontSize: 18 }} />
            </IconButton>
          )}
          <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
            <input
              ref={dateInputRef}
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                opacity: 0,
                pointerEvents: "none",
                border: "none",
                margin: 0,
                padding: 0,
              }}
              tabIndex={-1}
              aria-hidden="true"
            />
            <Tooltip title="Filtrar por data" placement="top">
              <IconButton
                size="small"
                onClick={() => {
                  if (dateInputRef.current) {
                    try {
                      dateInputRef.current.showPicker();
                    } catch {
                      dateInputRef.current.focus();
                      dateInputRef.current.click();
                    }
                  }
                }}
                style={{ padding: 4, color: selectedDate ? "#00a884" : "inherit" }}
              >
                <EventIcon style={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>

      {selectedDate && (
        <div style={{ padding: "0 14px 6px", display: "flex", alignItems: "center" }}>
          <Chip
            size="small"
            icon={<EventIcon style={{ fontSize: 16 }} />}
            label={`Data: ${selectedDate.split("-").reverse().join("/")}`}
            onDelete={() => {
              setSelectedDate("");
              if (!searchParam) setTab("open");
            }}
            color="primary"
          />
        </div>
      )}

      {/* Filter Chips */}
      <div className={classes.chipsContainer}>
        <Chip
          label="Tudo"
          size="small"
          className={`${classes.chip} ${isChipActive("open", "all") ? classes.chipActive : ""}`}
          onClick={() => handleChipClick("open", "all")}
        />
        <Chip
          label={
            openCount > 0
              ? `Atendendo ${openCount}`
              : "Atendendo"
          }
          size="small"
          className={`${classes.chip} ${isChipActive("open", "open") ? classes.chipActive : ""}`}
          onClick={() => handleChipClick("open", "open")}
        />
        <Chip
          label={
            pendingCount > 0
              ? `Aguardando ${pendingCount}`
              : "Aguardando"
          }
          size="small"
          className={`${classes.chip} ${isChipActive("open", "pending") ? classes.chipActive : ""}`}
          onClick={() => handleChipClick("open", "pending")}
        />
        {setGroupBox && (
          <Chip
            label="Grupos"
            size="small"
            className={`${classes.chip} ${isChipActive("group") ? classes.chipActive : ""}`}
            onClick={() => handleChipClick("group")}
          />
        )}
        {setClosedBox && (
          <Chip
            label="Finalizadas"
            size="small"
            className={`${classes.chip} ${isChipActive("closed") ? classes.chipActive : ""}`}
            onClick={() => handleChipClick("closed")}
          />
        )}
        {hasAIPlan && (
          <Chip
            label={
              aiCount > 0
                ? `🤖 Atendendo IA ${aiCount}`
                : "🤖 Atendendo IA"
            }
            size="small"
            className={`${classes.chipAI} ${isChipActive("open", "ai") ? classes.chipAIActive : ""}`}
            onClick={() => handleChipClick("open", "ai")}
          />
        )}
      </div>

      {/* Tab Panels */}
      <TabPanel value={tab} name="open" className={classes.ticketsWrapper}>
        <Paper className={tabOpen === "all" ? `${classes.ticketsWrapper} ${classes.unifiedScroll}` : classes.ticketsWrapper}>
          <TicketsList
            status="open"
            showAll={showAllTickets}
            selectedQueueIds={selectedQueueIds}
            selectedQueueOptionIds={selectedQueueOptionIds}
            updateCount={(val) => setOpenCount(val)}
            style={applyPanelStyle("open")}
            hideNoTickets={tabOpen === "all" || tabOpen === "ai"}
            showNewsletters={setNewsletterBox}
            inline={tabOpen === "all"}
            sectionLabel={tabOpen === "all" ? "Atendendo" : ""}
          />
          <TicketsList
            status="pending"
            selectedQueueIds={selectedQueueIds}
            selectedQueueOptionIds={selectedQueueOptionIds}
            updateCount={(val) => setPendingCount(val)}
            style={applyPanelStyle("pending")}
            hideNoTickets={(tabOpen === "all" && openCount > 0) || tabOpen === "ai"}
            showNewsletters={setNewsletterBox}
            inline={tabOpen === "all"}
            sectionLabel={tabOpen === "all" ? "Aguardando" : ""}
          />
          {tabOpen === "ai" && (
            <TicketsList
              status="open"
              showAll={showAllTickets}
              selectedQueueIds={selectedQueueIds}
              selectedQueueOptionIds={selectedQueueOptionIds}
              updateCount={(val) => setAiCount(val)}
              filterAI={true}
              showNewsletters={setNewsletterBox}
            />
          )}
        </Paper>
      </TabPanel>

      <TabPanel value={tab} name="group" className={classes.ticketsWrapper}>
        <Paper className={classes.ticketsWrapper}>
          <TicketsListGroup
            status="open"
            showAll={showAllTickets}
            selectedQueueIds={selectedQueueIds}
            selectedQueueOptionIds={selectedQueueOptionIds}
            updateCount={(val) => setOpenCount(val)}
            style={applyPanelStyle("open")}
          />
          <TicketsListGroup
            status="pending"
            selectedQueueIds={selectedQueueIds}
            selectedQueueOptionIds={selectedQueueOptionIds}
            updateCount={(val) => setPendingCount(val)}
            style={applyPanelStyle("pending")}
          />
        </Paper>
      </TabPanel>

      <TabPanel value={tab} name="closed" className={classes.ticketsWrapper}>
        <TicketsList
          status="closed"
          showAll={true}
          selectedQueueIds={selectedQueueIds}
          selectedQueueOptionIds={selectedQueueOptionIds}
          showNewsletters={setNewsletterBox}
          style={{ flex: 1, height: "100%" }}
        />
      </TabPanel>

      <TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
        <TagsFilter onFiltered={handleSelectedTags} />
        {profile === "admin" && (
          <UsersFilter onFiltered={handleSelectedUsers} />
        )}
        <TicketsList
          searchParam={searchParam}
          date={selectedDate}
          showAll={true}
          tags={selectedTags}
          users={selectedUsers}
          selectedQueueIds={selectedQueueIds}
          selectedQueueOptionIds={selectedQueueOptionIds}
          showNewsletters={setNewsletterBox}
          style={{ flex: 1, height: "100%" }}
        />
      </TabPanel>
    </Paper>
  );
};

export default TicketsManagerTabs;
